// ================================================================
// DIALOG COMPONENT: ChangePasswordDialog
// ================================================================
// Themed like Login/Forgot pages. Form uses Formik + Yup.
// API integration is stubbed; wire later inside handleSubmit.
// ================================================================

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import type { ChangePasswordFormValues } from '@/types/auth/password';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useChangePasswordMutation } from '@/services/passwordService';
import { Alert, AlertDescription } from '@/components/ui/alert';

type ChangePasswordDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userType?: 'self' | 'team';
};

export function ChangePasswordDialog({
  open,
  onOpenChange,
  userType = 'self',
}: ChangePasswordDialogProps) {
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const initialValues: ChangePasswordFormValues = {
    confirm_password: '',
    current_password: '',
    new_password: '',
    user_type: userType,
  };

  const schema = Yup.object({
    current_password: Yup.string().required('Current password is required'),
    new_password: Yup.string().min(6, 'Min 6 characters').required('New password is required'),
    confirm_password: Yup.string()
      .oneOf([Yup.ref('new_password')], 'Passwords must match')
      .required('Confirm password is required'),
    user_type: Yup.mixed<'self' | 'team'>()
      .oneOf(['self', 'team'])
      .required('User type is required'),
  });

  const [changePassword, { isLoading, error: changeError }] = useChangePasswordMutation();

  // Extract human readable error message (RTK Query variants)
  const getErrorMessage = (err: any): string | null => {
    if (!err) return null;
    return (
      err?.data?.message ||
      err?.data?.error ||
      err?.error ||
      err?.message ||
      (typeof err === 'string' ? err : null)
    );
  };
  const errorMessage = getErrorMessage(changeError);

  const handleSubmit = async (values: ChangePasswordFormValues) => {
    const payload = {
      oldPassword: values.current_password,
      newPassword: values.new_password,
      userType: values.user_type,
    };
    try {
      await changePassword(payload).unwrap();
      onOpenChange(false);
    } catch (e) {
      // Error handled globally
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] gap-3 overflow-y-auto border border-[#0f80a5]/10 bg-white/95 p-4 backdrop-blur-sm sm:max-w-md sm:gap-4 sm:p-6">
        <DialogHeader className="space-y-1.5 sm:space-y-2">
          <DialogTitle className="text-xl font-bold text-cyan-600 sm:text-2xl">
            Change Password
          </DialogTitle>
          <DialogDescription className="text-sm text-[#2A2F4A]/60 sm:text-base">
            Enter your current password and a new password to update your credentials.
          </DialogDescription>
        </DialogHeader>

        <Formik initialValues={initialValues} validationSchema={schema} onSubmit={handleSubmit}>
          {({ isSubmitting, values, setFieldValue }) => (
            <Form className="space-y-3 sm:space-y-4">
              {/* Current Password */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-[#2A2F4A] sm:text-sm">
                  Current Password
                </Label>
                <div className="relative">
                  <Field
                    as={Input}
                    name="current_password"
                    type={showCurrent ? 'text' : 'password'}
                    placeholder="Current Password"
                    className="h-10 rounded-xl border border-gray-200 bg-white pr-10 text-sm focus:border-[#118AB2] focus:ring-[#118AB2] sm:h-11 sm:text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 touch-manipulation p-1 text-gray-500 hover:text-gray-700 sm:right-3"
                    aria-label="Toggle current password visibility"
                  >
                    {showCurrent ? (
                      <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                    ) : (
                      <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                    )}
                  </button>
                </div>
                <ErrorMessage
                  name="current_password"
                  component="div"
                  className="mt-1 text-xs text-red-600"
                />
              </div>

              {/* New Password */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-[#2A2F4A] sm:text-sm">
                  New Password
                </Label>
                <div className="relative">
                  <Field
                    as={Input}
                    name="new_password"
                    type={showNew ? 'text' : 'password'}
                    placeholder="New Password"
                    className="h-10 rounded-xl border border-gray-200 bg-white pr-10 text-sm focus:border-[#118AB2] focus:ring-[#118AB2] sm:h-11 sm:text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 touch-manipulation p-1 text-gray-500 hover:text-gray-700 sm:right-3"
                    aria-label="Toggle new password visibility"
                  >
                    {showNew ? (
                      <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                    ) : (
                      <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                    )}
                  </button>
                </div>
                <ErrorMessage
                  name="new_password"
                  component="div"
                  className="mt-1 text-xs text-red-600"
                />
              </div>

              {/* Confirm Password */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-[#2A2F4A] sm:text-sm">
                  Confirm Password
                </Label>
                <div className="relative">
                  <Field
                    as={Input}
                    name="confirm_password"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Confirm Password"
                    className="h-10 rounded-xl border border-gray-200 bg-white pr-10 text-sm focus:border-[#118AB2] focus:ring-[#118AB2] sm:h-11 sm:text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 touch-manipulation p-1 text-gray-500 hover:text-gray-700 sm:right-3"
                    aria-label="Toggle confirm password visibility"
                  >
                    {showConfirm ? (
                      <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                    ) : (
                      <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                    )}
                  </button>
                </div>
                <ErrorMessage
                  name="confirm_password"
                  component="div"
                  className="mt-1 text-xs text-red-600"
                />
              </div>

              {/* User Type (Self only visible, but keep radio for parity with image) */}
              <div className="space-y-2">
                <Label className="text-xs font-medium text-[#2A2F4A] sm:text-sm">User Type</Label>
                <div className="flex items-center gap-4 sm:gap-6">
                  <label className="flex cursor-pointer items-center gap-2 text-xs sm:text-sm">
                    <input
                      type="radio"
                      name="user_type"
                      value="self"
                      checked={values.user_type === 'self'}
                      onChange={() => setFieldValue('user_type', 'self')}
                      className="h-4 w-4 cursor-pointer accent-[#0f80a5] sm:h-5 sm:w-5"
                    />
                    Self
                  </label>
                  <label className="flex cursor-not-allowed items-center gap-2 text-xs opacity-60 sm:text-sm">
                    <input
                      type="radio"
                      name="user_type"
                      value="team"
                      disabled
                      className="h-4 w-4 accent-[#0f80a5] sm:h-5 sm:w-5"
                    />
                    Team
                  </label>
                </div>
              </div>

              {errorMessage && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertDescription className="text-xs font-medium text-red-700 sm:text-sm">
                    {errorMessage}
                  </AlertDescription>
                </Alert>
              )}
              <DialogFooter className="pt-1 sm:pt-2">
                <Button
                  type="submit"
                  className="inline-flex h-10 w-full touch-manipulation items-center justify-center bg-[#0f80a5] text-sm font-medium hover:bg-[#0d7fa1] sm:h-11 sm:text-base"
                  disabled={isSubmitting || isLoading}
                >
                  {(isSubmitting || isLoading) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  )}
                  <span>Change Password</span>
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
